/**
 * 日志级别枚举：数值越大越详细。
 *
 * 过滤规则：仅当 `record.level <= effectiveLevel` 时输出。
 * 生产默认 SILENT，开发默认 WARN。
 */
export enum LogLevel {
  SILENT = 0,
  ERROR = 1,
  WARN = 2,
  INFO = 3,
  DEBUG = 4,
}

/** LogLevel 的字符串别名，供宿主配置使用 */
export type LogLevelName = 'silent' | 'error' | 'warn' | 'info' | 'debug'

const NAME_TO_LEVEL: Record<LogLevelName, LogLevel> = {
  silent: LogLevel.SILENT,
  error: LogLevel.ERROR,
  warn: LogLevel.WARN,
  info: LogLevel.INFO,
  debug: LogLevel.DEBUG,
}

const LEVEL_TO_NAME: Record<LogLevel, string> = {
  [LogLevel.SILENT]: 'SILENT',
  [LogLevel.ERROR]: 'ERROR',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.DEBUG]: 'DEBUG',
}

/**
 * 将级别枚举或字符串别名归一化为 LogLevel。
 *
 * @param value - LogLevel 枚举值或 'silent' | 'error' | 'warn' | 'info' | 'debug'
 * @param fallback - 无法解析时的回退级别，默认 WARN
 * @returns 归一化后的 LogLevel
 */
export function parseLogLevel(
  value: LogLevel | LogLevelName | string | undefined,
  fallback: LogLevel = LogLevel.WARN
): LogLevel {
  if (typeof value === 'number') {
    return value
  }
  if (typeof value === 'string') {
    const level = NAME_TO_LEVEL[value.toLowerCase() as LogLevelName]
    if (level !== undefined) {
      return level
    }
  }
  return fallback
}

/**
 * 将 LogLevel 转为大写名称，用于日志行前缀输出。
 *
 * @param level - 日志级别
 * @returns 级别名称，如 'WARN'
 */
export function logLevelName(level: LogLevel): string {
  return LEVEL_TO_NAME[level] ?? String(level)
}
